import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SessionCard from "./SessionCard";
import styles from "./timeTableStyles";
import {
  fetchTimeTable,
  getSessionRemainingSlots,
} from "./timeTableService";
import { formatDayDate } from "../../../utils/dateTimeUtils";
import { BRAND_COLOR } from "../../../common/constants";

const TimeTable = () => {
  const storedUser = localStorage.getItem("user");
  const userId = storedUser ? JSON.parse(storedUser).id : null;

  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDays, setExpandedDays] = useState({});
  const [hoveredDay, setHoveredDay] = useState(null);

  /* ===============================
     RESPONSIVE
  =============================== */
  const isMobile = window.innerWidth <= 768;

  const loadSessions = async () => {
    try {
      const response = await fetchTimeTable();
      if (!response.data?.length) {
        setDays([]);
        return;
      }

      const daysWithSessions = await Promise.all(
        response.data.map(async (day) => {
          const sessions = await Promise.all(
            day.sessions.map(async (session) => {
              try {
                const res = await getSessionRemainingSlots(session.id, userId);
                return {
                  ...session,
                  ...res.data,
                  remainingSlots:
                    res.data.remainingSlots ?? session.totalSlots,
                  agendas: res.data.agendas || [],
                };
              } catch {
                return { ...session, agendas: [] };
              }
            })
          );

          sessions.sort(
            (a, b) =>
              new Date(a.startTime).getTime() -
              new Date(b.startTime).getTime()
          );

          return { ...day, sessions };
        })
      );

      daysWithSessions.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      setDays(daysWithSessions);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
  }, []);

  const toggleDay = (dayId) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dayId]: !prev[dayId],
    }));
  };

  const refreshSessionStatus = async () => {
    loadSessions();
  };

  if (loading) {
    return (
      <div style={isMobile ? styles.mobile.pageContainer : styles.pageContainer}>
        <div style={styles.loadingBox}>Loading live sessions…</div>
      </div>
    );
  }

  return (
    <div style={isMobile ? styles.mobile.pageContainer : styles.pageContainer}>
      <h2 style={isMobile ? styles.mobile.heading : styles.heading}>
        Upcoming Live Sessions
      </h2>

      {/* How It Works Banner */}
      <div style={{
        backgroundColor: "#f0f9ff",
        border: `2px solid ${BRAND_COLOR}`,
        borderRadius: "12px",
        padding: isMobile ? "16px" : "20px",
        marginBottom: "24px",
        textAlign: "center"
      }}>
        <p style={{
          margin: "0 0 12px 0",
          fontSize: isMobile ? "14px" : "16px",
          color: "#333"
        }}>
          🤔 New to Speak Bold? Learn how our sessions work!
        </p>
        <Link
          to="/how-it-works"
          style={{
            backgroundColor: BRAND_COLOR,
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            textDecoration: "none",
            fontSize: isMobile ? "14px" : "15px",
            fontWeight: "600",
            display: "inline-block"
          }}
        >
          📖 How Does It Work?
        </Link>
      </div>

      {days.length === 0 ? (
        <div style={styles.emptyState}>No sessions available</div>
      ) : (
        days.map((day) => {
          const isExpanded = expandedDays[day.id];

          return (
            <div
              key={day.id}
              style={isMobile ? styles.mobile.dayCard : styles.dayCard}
            >
              {/* DAY HEADER */}
              <div
                onClick={() => toggleDay(day.id)}
                onMouseEnter={() => setHoveredDay(day.id)}
                onMouseLeave={() => setHoveredDay(null)}
                style={{
                  ...(isMobile
                    ? styles.mobile.dayHeader
                    : styles.dayHeader),
                  ...(isExpanded ? styles.dayHeaderExpanded : {}),
                  ...(hoveredDay === day.id && !isExpanded
                    ? styles.dayHeaderHover
                    : {}),
                }}
              >
                <span
                  style={
                    isMobile
                      ? styles.mobile.dayHeaderText
                      : styles.dayHeaderText
                  }
                >
                  {formatDayDate(day.date)}
                </span>

                <span
                  style={{
                    ...styles.arrow,
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  ▼
                </span>
              </div>

              {/* SESSIONS */}
              {isExpanded && (
                <div
                  style={
                    isMobile
                      ? styles.mobile.sessionList
                      : styles.sessionList
                  }
                >
                  {day.sessions.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      userId={userId}
                      refreshSessionStatus={refreshSessionStatus}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default TimeTable;
